import { useFormContext } from 'react-hook-form';

import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
} from '@/components/ui/form';
import { cn } from '@/lib/utils';
import type { GeneralFormProps } from '@/types/general';

import { Checkbox } from '../ui/checkbox';

type RHFShadcnCheckboxArrayProps = {
  name: string;
  value: string; // The value to add/remove from the array
  title: string;
  description?: string;
  /**
   * Canonical helper text shown beneath the label.
   * Falls back to `description` for backward compatibility.
   */
  helperText?: string;
  disabled?: boolean;
  required?: boolean;
  onChange?: (checked: boolean, value: string) => void;
  className?: string;
} & Omit<GeneralFormProps, 'value' | 'onChange'>;

function RHFShadcnCheckboxArray({
  name,
  title,
  description,
  helperText,
  value,
  required,
  onChange: externalOnChange,
  disabled,
  className,
}: RHFShadcnCheckboxArrayProps) {
  const { control } = useFormContext();
  const resolvedHelperText = helperText ?? description;

  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => {
        const currentValue = field.value as string[] || [];
        const isChecked = currentValue.includes(value);
        const checkboxId = `checkbox-${field.name.replace(/\./g, '-')}-${value}`;

        const handleChange = (checked: boolean) => {
          let newValue: string[];

          if (checked === true) {
            // Add the value to the array if not already present
            newValue = currentValue.includes(value)
              ? currentValue
              : [...currentValue, value];
          } else {
            // Remove the value from the array
            newValue = currentValue.filter(item => item !== value);
          }

          if (externalOnChange) {
            externalOnChange(checked, value);
          } else {
            field.onChange(newValue);
          }
        };

        return (
          <FormItem className={cn('flex w-full flex-row items-start gap-3', className)}>
            <FormControl>
              <Checkbox
                id={checkboxId}
                disabled={disabled}
                required={required}
                data-testid={`${field.name}-${value}`}
                className={disabled === true ? 'cursor-not-allowed opacity-50' : ''}
                onCheckedChange={handleChange}
                checked={isChecked}
              />
            </FormControl>
            <div
              className={cn(
                'flex flex-col gap-1',
                disabled === true && 'text-muted-foreground',
              )}
            >
              <FormLabel
                htmlFor={checkboxId}
                className={cn(
                  'type-label-l leading-none',
                  disabled === true ? 'cursor-not-allowed' : 'cursor-pointer',
                )}
              >
                {title}
                {required === true && <span className="ml-0.5 text-error">*</span>}
              </FormLabel>
              {resolvedHelperText !== undefined && resolvedHelperText !== '' && (
                <FormDescription className="type-body-s text-on-surface-variant">
                  {resolvedHelperText}
                </FormDescription>
              )}
            </div>
          </FormItem>
        );
      }}
    />
  );
}

export default RHFShadcnCheckboxArray;
